import { User } from 'features/users/types'
import { hasEditionRole } from 'features/users/model/user'
import {
  BoundSqlParameter,
  Map,
  MapConfig,
  MapDataset,
  MapPrivacy,
  MapType,
  SqlParameter,
  SqlParameterItem,
  SqlParameterType
} from './types'
import { WidgetType } from 'features/builder'
import {
  safeParseSqlDateRangeParameter,
  safeParseSqlNumericParameter,
  safeParseSqlNumericRangeParameter
} from 'features/builder/utils/sqlParameterUtils'
import { all, call, put, spawn } from 'redux-saga/effects'
import { setGeocoderSearchText } from 'features/builder/state/builderSlice'
import { getMapCredentials } from 'features/builder/api/builder'
import { API_VERSIONS, Credentials } from '@carto/react-api'
import { GeocoderResult, getGeocoderResult } from 'features/builder/ui/BuilderAddressSearch/BuilderGeocoderWidget'
import {
  getColumnAliasByWidget,
  getFilterByWidget,
  widgetTypeToFilterType
} from 'features/builder/state/builderSliceUtils'
import { Filter } from '@carto/react-redux'
import { parseQuery } from 'features/builder/utils/queryParametersUtils'
import { updateCurrentMapDataset } from './state/mapsSlice'
import { setGeocoderResult } from '@carto/react-widgets'
import { BuilderPaths } from 'features/builder/paths'
import { safeParseLatLng } from 'features/builder/utils/searchParamsUtils'

export const getMapUrlForUser = (map: Map, user?: User) => {
  if (!map.id || !user) return

  const isPublic = map.token && map.privacy === MapPrivacy.Public
  const isShared = map.token && map.privacy === MapPrivacy.Shared
  const isUserOwner = user?.user_id === map.ownerId
  const isEditor = hasEditionRole(user)

  if (isUserOwner || (isEditor && map.collaborative)) {
    return BuilderPaths.EditMap(map.id)
  } else if (isPublic || isShared) {
    return BuilderPaths.ViewMap(map.id)
  }
}

export function setDefaultMapConfig(map: Map, defaultConfig: MapConfig) {
  if (!map.keplerMapConfig?.config) return
  if (defaultConfig.lat) {
    map.keplerMapConfig.config.mapState.latitude = defaultConfig.lat
  }
  if (defaultConfig.lng) {
    map.keplerMapConfig.config.mapState.longitude = defaultConfig.lng
  }
  if (defaultConfig.zoom) {
    map.keplerMapConfig.config.mapState.zoom = defaultConfig.zoom
  }
  if (defaultConfig.bearing) {
    map.keplerMapConfig.config.mapState.bearing = defaultConfig.bearing
  }
  if (defaultConfig.pitch) {
    map.keplerMapConfig.config.mapState.pitch = defaultConfig.pitch
  }
  if (defaultConfig.layers?.length && map.keplerMapConfig?.config?.visState.layers) {
    const visState = map.keplerMapConfig?.config?.visState
    if (defaultConfig.layers.some((index) => index < visState.layers.length)) {
      visState.layers = visState.layers.map((l, idx) => ({
        ...l,
        config: {
          ...l.config,
          isVisible: defaultConfig.layers?.includes(idx)
        }
      }))
    }
  }
}

export function setViewerDefaultMapConfig(map: Map, mapDatasets: MapDataset[], defaultConfig: MapConfig) {
  if (!map.keplerMapConfig?.config) return
  if (defaultConfig.filters) {
    patchWidgetFilters(map, mapDatasets, defaultConfig.filters)
  }
  if (defaultConfig.spatialFilter) {
    map.keplerMapConfig.config.spatialFilter = defaultConfig.spatialFilter
  }
}

/** Updates SQL parameters in map and dataset configurations using URL parameters (?param_type=X). */
export function* setViewerDefaultSqlParameters(map: Map, mapDatasets: MapDataset[], defaultConfig: MapConfig) {
  const sqlParameters = map.keplerMapConfig?.config.sqlParameters
  const values = defaultConfig.sqlParameters
  if (!sqlParameters || !values) return

  for (const param of sqlParameters as BoundSqlParameter[]) {
    switch (param.type) {
      case SqlParameterType.Category:
        if (values[param.item.sqlName]) {
          setValue(param, param.item, values[param.item.sqlName], (s: string[]) => s)
        }
        break

      case SqlParameterType.Numeric:
        if (values[param.item.sqlName]) {
          setValue(param, param.item, values[param.item.sqlName][0], safeParseSqlNumericParameter)
        }
        break

      case SqlParameterType.NumericRange:
        if (values[param.start.sqlName]) {
          setValue(param, param.start, values[param.start.sqlName][0], safeParseSqlNumericRangeParameter)
        }
        if (values[param.end.sqlName]) {
          setValue(param, param.end, values[param.end.sqlName][0], safeParseSqlNumericRangeParameter)
        }
        break

      case SqlParameterType.DateRange:
        if (values[param.start.sqlName]) {
          setValue(param, param.start, values[param.start.sqlName][0], safeParseSqlDateRangeParameter)
        }
        if (values[param.end.sqlName]) {
          setValue(param, param.end, values[param.end.sqlName][0], safeParseSqlDateRangeParameter)
        }
        break
    }
  }

  try {
    yield all(
      mapDatasets
        .filter((dataset) => !!dataset.queryTemplate)
        .map((dataset) =>
          call(function* () {
            const { queryParameters } = parseQuery({
              query: dataset.queryTemplate as string,
              sqlParameters: sqlParameters,
              provider: dataset.providerId
            })
            yield put(
              updateCurrentMapDataset({
                id: dataset.id,
                dataset: {
                  queryParameters
                }
              })
            )
          })
        )
    )
  } catch (e) {
    console.error(e)
  }

  function setValue<P extends SqlParameter, In, Out>(
    parameter: P,
    item: SqlParameterItem<Out>,
    input: In | undefined,
    safeParse: (value: In, param: P) => Out | null
  ): void {
    const value = input ? safeParse(input, parameter) : null
    if (!value) return
    item.value = value
  }
}

export function* setViewerDefaultSearch(map: Map, defaultConfig: MapConfig) {
  try {
    const mapState = map.keplerMapConfig?.config.mapState as { latitude: number; longitude: number }
    const credentials = yield call(getMapCredentials, map.token) as Credentials
    credentials.apiVersion = API_VERSIONS.V3
    const searchText = defaultConfig?.search as string
    const passive = typeof defaultConfig?.lat === 'number' && typeof defaultConfig?.lng === 'number'

    // BuilderGeocoderWidget will set `searchText` state too, *if* the searchbar is enabled. Because
    // the searchbar might not be enabled, state should also be set here so the URL param isn't lost.
    yield put(setGeocoderSearchText(searchText))

    // Set pin from `?search=<lat,lng>` parameter.
    const latLng = safeParseLatLng(searchText)
    if (latLng) {
      mapState.latitude = latLng.latitude
      mapState.longitude = latLng.longitude
      yield put(setGeocoderResult({ ...latLng, passive }))
      return
    }

    // Geocoding should block displaying the map only if it affects viewport location.
    if (passive) {
      yield spawn(function* () {
        const result: GeocoderResult | null = yield call(getGeocoderResult, credentials, { searchText })
        if (result) yield put(setGeocoderResult({ ...result, passive }))
      })
    } else {
      const result: GeocoderResult | null = yield call(getGeocoderResult, credentials, { searchText })
      if (result) {
        mapState.latitude = result.latitude
        mapState.longitude = result.longitude
        yield put(setGeocoderResult({ ...result, passive }))
      }
    }
  } catch (e) {
    console.error(e)
  }
}

function patchWidgetFilters(map: Map, mapDatasets: MapDataset[], values: Record<string, string[]>) {
  const widgets = map.keplerMapConfig?.config.widgets
  const filters = map.keplerMapConfig?.config.filters
  if (!widgets || !filters) return

  for (const widget of widgets) {
    const dataset = mapDatasets.find(({ id }) => widget.dataSource === id)
    if (!values[widget.id] || !widget.isValid || !widget.column || !dataset) continue

    const widgetValues = values[widget.id]
    const filterType = widgetTypeToFilterType[widget.type]
    if (!filterType) continue

    const columnAlias = getColumnAliasByWidget(widget, dataset)
    if (!columnAlias) continue

    let sourceFilter = getFilterByWidget(widget, dataset, filters)
    if (!sourceFilter) {
      filters[widget.dataSource] ||= {}
      filters[widget.dataSource][columnAlias] ||= {}
      filters[widget.dataSource][columnAlias][filterType] ||= { owner: widget.id, values: [] }
      sourceFilter = filters[widget.dataSource][columnAlias][filterType] as Filter
    }

    switch (widget.type) {
      case WidgetType.CATEGORY:
      case WidgetType.PIE:
        sourceFilter.values = dataset.type === MapType.raster ? widgetValues.map(Number) : widgetValues
        break

      case WidgetType.HISTOGRAM:
      case WidgetType.RANGE:
        sourceFilter.values = widgetValues.map((value) => toRange(value).map(Number))
        break

      case WidgetType.TABLE:
      case WidgetType.FORMULA:
      case WidgetType.TIMESERIES:
      default:
        break
    }

    sourceFilter.owner = widget.id
  }

  function toRange(str: string): [string, string] {
    return str.split('-').slice(0, 2) as [string, string]
  }
}

export function mapHasUpdatePending(map: Map) {
  return (
    (map.privacy === MapPrivacy.Shared || map.privacy === MapPrivacy.Public) &&
    map.updatedAt &&
    map.lastPublishedAt &&
    map.lastPublishedAt < map.updatedAt
  )
}

export function userCanViewMap(map: Map, user: User | undefined) {
  return (
    map.privacy === MapPrivacy.Public ||
    (user &&
      (map.ownerId === user.user_id ||
        (map.privacy === MapPrivacy.Shared && map.accountId === user.user_metadata.account_id)))
  )
}

export function userCanEditMap(map: Map, user: User) {
  return (
    map.ownerId === user.user_id ||
    (map.collaborative &&
      (map.privacy === MapPrivacy.Shared || map.privacy === MapPrivacy.Public) &&
      hasEditionRole(user) &&
      map.accountId === user.user_metadata.account_id)
  )
}
